import { InputProps } from '@chakra-ui/react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ChargePeriod, TariffType, TOURateType } from 'clipsal-cortex-types/src/api/api-tariffs-v2';

import { COMPONENT_MIN_HEIGHT, TOP_NAV_SPACING_AFFORDANCE } from '../../common/constants';
import { selectSite } from '../site/siteSlice';
import { getDefaultFlatTariffRates } from './flat-tariff/flat-tariff-helpers';
import { useTranslatedEmptySeasonsTemplate } from './tariff-season/tariff-season-helpers';
import { Rate, TariffData, TariffRatesPerType, TariffTypeConfig } from './tariff-types';
import { useTariffs } from './tariffApi';
import { getDefaultTieredTariffRates } from './tiered-tariff/tiered-tariff-helpers';
import { TOUTariffFormKey } from './tou-tariff/tou-tariff-constants';
import { getDefaultTOUTariffRates } from './tou-tariff/tou-tariff-helpers';

export const TARIFF_TYPE_CONFIG: TariffTypeConfig[] = [
  {
    type: 'FLAT',
  },
  {
    type: 'TIERED',
  },
  {
    type: 'TOU',
  },
];

export const DEFAULT_MONTHLY_DELIVERY_CHARGE_RATE: Rate = {
  chargeType: 'FIXED_PRICE',
  chargeClass: 'DISTRIBUTION',
  chargePeriod: 'MONTHLY',
  rateBands: [
    {
      sequenceNumber: 1,
      rate: undefined,
      hasConsumptionLimit: false,
      hasDemandLimit: false,
    },
  ],
};

// This hook helps set up sensible default form values when setting up a new tariff.
export const useTranslatedDefaultTariffData = (): TariffData => {
  const site = useSelector(selectSite);
  const { tariffs } = useTariffs();
  const EMPTY_SEASONS_TEMPLATE = useTranslatedEmptySeasonsTemplate();
  const DEFAULT_TARIFF_DATA = {
    effectiveDate: format(new Date(), 'yyyy-MM-dd'),
    tariff: {
      tariffType: 'FLAT' as TariffType,
      planName: '',
      deliveryCharge: DEFAULT_MONTHLY_DELIVERY_CHARGE_RATE,
      rates: [getDefaultFlatTariffRates(1), getDefaultFlatTariffRates(2)],
      seasons: EMPTY_SEASONS_TEMPLATE,
      retailer: {
        id: 0,
        name: '',
      },
      utility: {
        id: null,
        name: '',
        state: '',
      },
      distributorId: 0,
      holidayCountry: '',
      holidaySubdiv: '',
    },
    zipCode: site.post_code,
  };

  const contextAwareDefaultTariffData = DEFAULT_TARIFF_DATA;

  // If the site has existing tariffs, we need to do some extra checks to ensure a sensible default for effective date.
  const isUniqueEffectiveDate = tariffs.every(
    (tariff) => tariff.tariff_effective_date !== DEFAULT_TARIFF_DATA.effectiveDate
  );
  // If there's no unique effective date (ie. we find there is an existing tariff with effective date of today) --
  // -- then we update the default effective date to be the next day.
  if (!isUniqueEffectiveDate) {
    const newEffectiveDate = new Date(tariffs[0].tariff_effective_date);
    newEffectiveDate.setDate(newEffectiveDate.getDate() + 1);
    contextAwareDefaultTariffData.effectiveDate = format(newEffectiveDate, 'yyyy-MM-dd');
  }

  return contextAwareDefaultTariffData;
};

export const TOU_RATE_TYPE_TO_FORM_TERM_MAP: Record<TOURateType, TOUTariffFormKey> = {
  PEAK: 'peak',
  OFF_PEAK: 'offPeak',
  PARTIAL_PEAK: 'partialPeak',
  SINGLE: 'peak', // not yet known how to handle single rate type
};

export const TARIFF_TO_DEFAULT_FUNCTION_MAP: Record<TariffType, (seasonIndex: number) => TariffRatesPerType> = {
  FLAT: getDefaultFlatTariffRates,
  TIERED: getDefaultTieredTariffRates,
  TOU: getDefaultTOUTariffRates,
  REAL_TIME: () => {
    throw new Error('SE Home does not support real-time rates!');
  },
};

export const CHARGE_PERIOD_TO_LABEL_MAP: Record<ChargePeriod, string> = {
  MONTHLY: 'Monthly',
  DAILY: 'Daily',
  QUARTERLY: 'Quarterly',
  ANNUALLY: 'Annually',
};

export const useTranslatedCommonInputProps = (): InputProps => {
  const { t } = useTranslation();
  return {
    borderRadius: '1px',
    borderColor: '#9FA0A4',
    py: 6,
    _dark: { background: '#27282A' },
    size: 'md',
    placeholder: `${t('Energy Rates.enter price per kwh in usd')}...`,
    _placeholder: { color: '#929497' },
    type: 'number',
    min: 0,
  };
};

export const MIN_TARIFF_PAGE_HEIGHT = `calc(${COMPONENT_MIN_HEIGHT} - ${TOP_NAV_SPACING_AFFORDANCE} - 5vh)`;

export const MAX_TARIFF_PAGE_WIDTH = `calc(100vw - env(safe-area-inset-left) - env(safe-area-inset-right))`;
